import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, Brush, ArrowRight } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { getContent } = useSiteContent();
  const [flashDesigns, setFlashDesigns] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [activeTab, setActiveTab] = useState('flash'); // 'flash' or 'tattoo'
  const [selectedSize, setSelectedSize] = useState('all');
  const [loading, setLoading] = useState(true);

  const sizes = ['all', 'small', 'medium', 'large'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [flashRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/flash`),
        axios.get(`${API}/portfolio`),
      ]);
      setFlashDesigns(flashRes.data);
      setPortfolioItems(portfolioRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDesigns = flashDesigns.filter(design => {
    return selectedSize === 'all' || design.size === selectedSize;
  });

  // Group designs by size
  const groupedDesigns = {
    small: filteredDesigns.filter(d => d.size === 'small'),
    medium: filteredDesigns.filter(d => d.size === 'medium'),
    large: filteredDesigns.filter(d => d.size === 'large')
  };

  return (
    <div data-testid="gallery-page" className="tattoo-bg-home min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <section className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl font-black mb-4 leading-none tracking-tight">
              {getContent('gallery_hero_title', 'F&T GALLERY')}
            </h1>
            <p className="font-mono text-sm uppercase tracking-widest text-gray-600">{getContent('gallery_hero_subtitle', 'Flash & Tattoo Collection')}</p>
          </motion.div>
        </section>

        {/* Category Tabs */}
        <section className="mb-10">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setActiveTab('flash')}
              data-testid="tab-flash"
              className={`flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 font-bold text-sm uppercase tracking-wider transition-all ${
                activeTab === 'flash'
                  ? 'bg-black text-white'
                  : 'border-2 border-black text-black hover:bg-black hover:text-white'
              }`}
            >
              <Sparkles size={18} />
              Flash Gallery
            </button>
            <button
              onClick={() => setActiveTab('tattoo')}
              data-testid="tab-tattoo"
              className={`flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 font-bold text-sm uppercase tracking-wider transition-all ${
                activeTab === 'tattoo'
                  ? 'bg-black text-white'
                  : 'border-2 border-black text-black hover:bg-black hover:text-white'
              }`}
            >
              <Brush size={18} />
              Tattoo Gallery
            </button>
          </div>
        </section>

        {/* Flash Gallery Tab */}
        {activeTab === 'flash' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Size Filter */}
            <section className="mb-10">
              <div className="paper-texture p-6 tape-corner">
                <label className="font-heading text-lg font-bold mb-4 block">Filter by Size</label>
                <div className="flex flex-wrap gap-3">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      data-testid={`size-filter-${size}`}
                      onClick={() => setSelectedSize(size)}
                      className={`px-5 py-2 font-bold text-xs uppercase tracking-wider transition-all ${
                        selectedSize === size
                          ? 'bg-black text-white'
                          : 'border-2 border-black text-black hover:bg-black hover:text-white'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Flash Gallery Grid */}
            <section>
              {loading ? (
                <div className="text-center py-12">
                  <p className="font-body text-lg">Loading gallery...</p>
                </div>
              ) : selectedSize === 'all' ? (
                <>
                  {groupedDesigns.small.length > 0 && (
                    <div className="mb-16">
                      <h2 className="font-heading text-3xl font-bold mb-6 border-b-2 border-black pb-2">SMALL</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {groupedDesigns.small.map((design, index) => (
                          <motion.div key={design.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="group cursor-pointer">
                            <div className="polaroid tape-top-left"><img src={design.image_url} alt={design.title} className="w-full h-64 object-cover" /></div>
                            <p className="font-heading text-lg font-bold mt-2">{design.title}</p>
                            <p className="font-mono text-xs uppercase text-gray-600">{design.style}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                  {groupedDesigns.medium.length > 0 && (
                    <div className="mb-16">
                      <h2 className="font-heading text-3xl font-bold mb-6 border-b-2 border-black pb-2">MEDIUM</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {groupedDesigns.medium.map((design, index) => (
                          <motion.div key={design.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="group cursor-pointer">
                            <div className="polaroid tape-corner"><img src={design.image_url} alt={design.title} className="w-full h-64 object-cover" /></div>
                            <p className="font-heading text-lg font-bold mt-2">{design.title}</p>
                            <p className="font-mono text-xs uppercase text-gray-600">{design.style}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                  {groupedDesigns.large.length > 0 && (
                    <div className="mb-16">
                      <h2 className="font-heading text-3xl font-bold mb-6 border-b-2 border-black pb-2">LARGE</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {groupedDesigns.large.map((design, index) => (
                          <motion.div key={design.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="group cursor-pointer">
                            <div className="polaroid tape-top-right"><img src={design.image_url} alt={design.title} className="w-full h-64 object-cover" /></div>
                            <p className="font-heading text-lg font-bold mt-2">{design.title}</p>
                            <p className="font-mono text-xs uppercase text-gray-600">{design.style}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : filteredDesigns.length === 0 ? (
                <div className="text-center py-12">
                  <p className="font-body text-lg text-gray-600">No designs found with selected filters.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredDesigns.map((design, index) => (
                    <motion.div key={design.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="group cursor-pointer">
                      <div className="polaroid tape-corner"><img src={design.image_url} alt={design.title} className="w-full h-64 object-cover" /></div>
                      <p className="font-heading text-lg font-bold mt-2">{design.title}</p>
                      <p className="font-mono text-xs uppercase text-gray-600">{design.style}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {filteredDesigns.length > 0 && (
                <div className="text-center mt-16">
                  <div className="paper-texture p-8 inline-block">
                    <h3 className="font-heading text-3xl font-bold mb-4">INTERESTED IN A DESIGN?</h3>
                    <p className="font-body text-lg mb-6">Book a consultation to claim your flash tattoo</p>
                    <Link to="/quote-estimator" data-testid="book-flash-button" className="inline-block bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all">
                      BOOK NOW →
                    </Link>
                  </div>
                </div>
              )}
            </section>
          </motion.div>
        )}

        {/* Tattoo Gallery Tab */}
        {activeTab === 'tattoo' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <section className="mb-10">
              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">Completed Works</h2>
                <p className="font-body text-gray-600 max-w-xl mx-auto">
                  Browse our portfolio of custom tattoos created for clients at Vision Tattoo.
                </p>
              </div>
            </section>

            {/* Portfolio Grid */}
            <section>
              {(() => {
                const allImages = portfolioItems.map((item) => ({
                  key: `p-${item.id}`,
                  image_url: item.image_url,
                  title: item.title,
                  category: item.category,
                }));

                if (loading) {
                  return (
                    <div className="text-center py-12">
                      <p className="font-body text-lg">Loading portfolio...</p>
                    </div>
                  );
                }
                if (allImages.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <p className="font-body text-lg text-gray-600">Portfolio coming soon...</p>
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="tattoo-portfolio-grid">
                    {allImages.map((img, index) => (
                      <motion.div
                        key={img.key}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        className="group"
                      >
                        <div className={`polaroid ${index % 3 === 0 ? 'tape-top-left' : index % 3 === 1 ? 'tape-corner' : 'tape-top-right'}`}>
                          <img
                            src={img.image_url}
                            alt={img.title || `Portfolio piece ${index + 1}`}
                            className="w-full h-72 object-cover"
                          />
                        </div>
                        {img.title && (
                          <p className="font-heading text-lg font-bold mt-2">{img.title}</p>
                        )}
                        {img.category && (
                          <p className="font-mono text-xs uppercase text-gray-600">{img.category}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                );
              })()}

              {/* CTA Section */}
              <div className="text-center mt-16">
                <div className="paper-texture p-8 inline-block">
                  <h3 className="font-heading text-3xl font-bold mb-4">WANT SOMETHING CUSTOM?</h3>
                  <p className="font-body text-lg mb-6">Let's create a unique piece just for you</p>
                  <Link to="/quote-estimator" data-testid="book-custom-button" className="inline-flex items-center gap-2 bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all">
                    START YOUR JOURNEY <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </section>
          </motion.div>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default GalleryPage;
